import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Stack } from '@mui/material';
import { CustomAlert, CustomButton, CustomTable, Text } from '../../shared/uiComponents';
import { Tab, TabsContainer } from '../../shared/uiComponents/Tabs';

import { Container } from '../../components/StyledComponents';
import { PageHeader } from '../../components/PageHeader';
import { useCheckPermission } from '../../shared/Helpers/hooks';
import { PERMISSIONS } from '../../App/constants';
import { PlusIcon, UploadIcon } from '../../shared/Assets/svg';
import { Settings } from '../../shared/Assets/menuIcons';
import { Headers, TableBody } from './tableInfo';
import AdpRateMappingForm from './form';

import { DispatchProperties, useSelector } from '../../redux/store';
import { getAdpRateMappings, setAdpRateMappingView } from '../../redux/State/clientSlice/adpRateMappingSlice';
import { AdpRateMappingProperties } from '../../redux/API/ClientAPIHelpers/adpRateMapping';
import ServiceCodes from './serviceCodes';
import ImportMappings from './importMappings';
import { TEXT } from '../../shared/Helpers/colors';

const AdpRateMappings = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const { permissionGranted } = useCheckPermission(PERMISSIONS.SUPERADMIN);

	const mappings = useSelector((state) => state.adpRateMapping.mappings);
	const loading = useSelector((state) => state.adpRateMapping.loading);
	const loadFailed = useSelector((state) => state.adpRateMapping.loadFailed);

	const [formOpen, setFormOpen] = useState(false);
	const [editItem, setEditItem] = useState<AdpRateMappingProperties | null>(null);
	const [tab, setTab] = useState(0);
	const [codesOpen, setCodesOpen] = useState(false);
	const [importOpen, setImportOpen] = useState(false);
	const visibleMappings = mappings.filter((mapping) =>
		tab === 1 ? Boolean(mapping.serviceCodeId) : !mapping.serviceCodeId,
	);

	useEffect(() => {
		if (!permissionGranted) return;
		dispatch(setAdpRateMappingView(tab === 1));
		dispatch(getAdpRateMappings(tab === 1));
	}, [dispatch, permissionGranted, tab]);

	if (!permissionGranted) return null;

	const handleAdd = () => {
		setEditItem(null);
		setFormOpen(true);
	};

	const handleEdit = (item: AdpRateMappingProperties) => {
		setEditItem(item);
		setFormOpen(true);
	};

	const handleClose = () => {
		setFormOpen(false);
		setEditItem(null);
	};

	return (
		<Container sx={{ minWidth: 0 }}>
			<PageHeader
				icon={<Settings />}
				title="ADP Rate Mappings"
				isInContainer
				customStyles={{ marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}
				headerContent={
					<Stack direction="row" alignItems="center" gap="12px" flexWrap="wrap">
						<CustomButton
							title="Import mappings"
							buttonType="secondaryGray"
							startIcon={<UploadIcon />}
							onClick={() => setImportOpen(true)}
						/>
						<CustomButton title="Add mapping" onClick={handleAdd} startIcon={<PlusIcon />} />
					</Stack>
				}
			/>
			<Stack direction="row" alignItems="center" gap="12px" flexWrap="wrap" justifyContent="space-between" mb="20px">
				<TabsContainer role="tablist" aria-label="ADP mapping type">
					{['By client', 'By service code'].map((label, index) => (
						<Tab
							as="button"
							role="tab"
							aria-selected={tab === index}
							selected={tab === index}
							key={label}
							style={{ cursor: 'pointer', font: 'inherit', color: 'inherit' }}
							onClick={() => {
								setTab(index);
								setEditItem(null);
								setFormOpen(false);
							}}
						>
							<Text title={label} type="psm" fontWeight={tab === index ? 'semiBold' : 'medium'} />
						</Tab>
					))}
				</TabsContainer>
				{tab === 1 && (
					<CustomButton title="Manage service codes" buttonType="tertiary" onClick={() => setCodesOpen(true)} />
				)}
			</Stack>
			<Text
				title={
					tab === 1 ? 'ADP Rate IDs for each provider and service code.' : 'ADP Rate IDs for each provider and client.'
				}
				type="psm"
				color={TEXT.SECONDARY}
				className="marginBottom16"
			/>
			{!loading && loadFailed ? (
				<Alert
					severity="error"
					action={<CustomButton title="Retry" buttonType="quaternary" onClick={() => dispatch(getAdpRateMappings())} />}
				>
					Mappings could not be loaded. Please try again.
				</Alert>
			) : (
				<CustomTable
					headers={Headers.map((header) =>
						header.id === '2' && tab === 1 ? { ...header, name: 'Service code' } : header,
					)}
					data={{
						query: visibleMappings.length ? visibleMappings : null,
					}}
					TableBody={(props: any) => <TableBody {...props} onEdit={handleEdit} />}
					loading={loading}
					hasPagination={false}
					hasMobile={false}
				/>
			)}
			{codesOpen && (
				<CustomAlert
					open
					onClose={() => {
						setCodesOpen(false);
						dispatch(getAdpRateMappings());
					}}
					title="Manage service codes"
					maxWidth="md"
					Content={ServiceCodes}
				/>
			)}
			{importOpen && (
				<CustomAlert
					open
					onClose={() => setImportOpen(false)}
					title="Import ADP mappings"
					maxWidth="md"
					Content={ImportMappings}
				/>
			)}
			{formOpen && (
				<AdpRateMappingForm open={formOpen} onClose={handleClose} editItem={editItem} serviceCode={tab === 1} />
			)}
		</Container>
	);
};

export default AdpRateMappings;
